import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import "../assets/variables.css";
import "../assets/users.css";
import "../assets/panels.css";
import { useAuth } from "../contexts/AuthContext";
import { FaPlus } from "react-icons/fa";
import Loader from "../components/Loader";
import { toast } from "react-toastify";
import S3ImageUpload from "../components/S3ImageUpload";

const UserCreate = () => {
  const navigate = useNavigate();

  // Role IDs matching Laravel UserFormController.js
  const ROLE_IDS = {
    GUARDIAN: 9,
    CLIENT: 8,
    CONTRACTOR: 10,
  };

  // Initial state matching Laravel form structure
  const [user, setUser] = useState({
    // Common fields (matching Laravel fields.blade.php)
    role_id: "",
    salutation: "",
    first_name: "",
    last_name: "",
    email: "",
    email_alternative: "",
    password: "",
    confirm_password: "",
    image: null,
    imageKey: null, // S3 key for uploaded image
    job_title: "",
    phone_number: "",
    mobile_number: "",
    address_1: "",
    address_2: "",
    address_3: "",
    town: "",
    postcode: "",
    fax_number: "",
    staff: 0,
    staff_guardian_page: 0,
    staff_property_owners: 0,
    staff_about_us_page: 0,
    can_review_certificates: 0,
    department_id: "",
    biography: "",
    color: "#000000",
    rec_notif_of_property_status: 0,
    rec_notif_of_property_id: 0,

    // Guardian fields (matching Laravel fields-guardian.blade.php)
    country_id: "",
    religion_id: "",
    hear_about_id: "",
    gender: "",
    gender_freetext: "",
    date_of_birth: "",
    national_insurance_number: "",
    criminal_record: 0,
    criminal_record_description: "",
    medical_condition: 0,
    medical_condition_description: "",
    next_of_kin: "",
    notes: "",

    // Client fields (matching Laravel fields-client-contact.blade.php)
    client_id: "", // Flattened for backend
    position: "",
    department: "",
    reports_to: "",
    description: "",
    access_to_ntq: 0,

    // Contractor fields (matching Laravel fields-contractor-contact.blade.php)
    contractor_id: "", // Flattened for backend
    contractor_position: "",
    contractor_department: "",
    contractor_reports_to: "",
    date_started_with_company: "",
  });

  const [dropdowns, setDropdowns] = useState({
    roles: [],
    nationalities: [],
    religions: [],
    clients: [],
    contractors: [],
    hearAbouts: [],
    departments: [],
    genders: [
      { id: 1, name: "Male" },
      { id: 2, name: "Female" },
      { id: 3, name: "Other" },
      { id: 4, name: "Prefer not to say" },
      { id: 5, name: "Non-binary" },
      { id: 6, name: "Let me Type*" },
    ],
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef();

  const { user: currentUser } = useAuth();

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/users/create-data`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then((res) => res.json())
      .then((data) => {
        // Merge API data with defaults to preserve genders and handle missing arrays
        setDropdowns((prev) => ({
          ...prev,
          roles: data.roles || [],
          nationalities: data.nationalities || [],
          religions: data.religions || [],
          clients: data.clients || [],
          contractors: data.contractors || [],
          hearAbouts: data.hearAbouts || [],
          departments: data.departments || [],
        }));
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load form data");
        setLoading(false);
      });
  }, []);

  // Helper functions for date conversion
  const convertToLaravelDate = (isoDate) => {
    if (!isoDate) return "";
    const [year, month, day] = isoDate.split("-");
    return `${day}/${month}/${year}`;
  };

  const convertToISODate = (laravelDate) => {
    if (!laravelDate) return "";
    const [day, month, year] = laravelDate.split("/");
    return `${year}-${month}-${day}`;
  };

  // Handle key press for phone number fields to prevent non-numeric input
  const handleKeyDown = (e) => {
    const { name } = e.target;
    if (name === "mobile_number" || name === "phone_number") {
      // Allow: numbers, space, hyphen, plus, parentheses, backspace, delete, tab, escape, enter, arrows
      const allowedKeys = ['0','1','2','3','4','5','6','7','8','9',' ','-','+','(',')'];
      const specialKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      
      // Allow Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
      if (e.ctrlKey && ['a', 'c', 'v', 'x', 'z'].includes(e.key.toLowerCase())) {
        return;
      }
      
      if (!allowedKeys.includes(e.key) && !specialKeys.includes(e.key)) {
        e.preventDefault();
      }
    }
  };

  // Handle paste event for phone number fields to clean pasted content
  const handlePaste = (e) => {
    const { name } = e.target;
    if (name === "mobile_number" || name === "phone_number") {
      e.preventDefault();
      const pastedText = e.clipboardData.getData('text');
      const cleanedText = pastedText.replace(/[^0-9\s\-+()]/g, '');
      console.log('Paste filtered:', pastedText, '->', cleanedText);
      
      setUser((prev) => ({
        ...prev,
        [name]: cleanedText,
      }));
    }
  };

  // Handle input event as a backup filter
  const handleInput = (e) => {
    const { name, value } = e.target;
    if (name === "mobile_number" || name === "phone_number") {
      const cleanedValue = value.replace(/[^0-9\s\-+()]/g, '');
      if (cleanedValue !== value) {
        console.log('Input event filtered:', value, '->', cleanedValue);
        e.target.value = cleanedValue;
        setUser((prev) => ({
          ...prev,
          [name]: cleanedValue,
        }));
      }
    }
  };

  // Helper for dot notation in handleChange
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // Special handling for date fields
    if (type === "date") {
      setUser((prev) => ({
        ...prev,
        [name]: value ? convertToLaravelDate(value) : "",
      }));
    } else if (name === "mobile_number" || name === "phone_number") {
      // Allow only numbers, spaces, hyphens, plus signs, and parentheses for phone fields
      const phoneValue = value.replace(/[^0-9\s\-+()]/g, '');
      console.log('Phone input filtered:', value, '->', phoneValue);
      setUser((prev) => ({
        ...prev,
        [name]: phoneValue,
      }));
    } else {
      setUser((prev) => {
        const newData = {
          ...prev,
          [name]: type === "checkbox" ? checked : value,
        };
        
        // Clear gender_freetext when gender is not "Let me Type*" (option 6)
        if (name === "gender" && parseInt(value) !== 6) {
          newData.gender_freetext = "";
        }
        
        return newData;
      });
    }
    
    // Clear validation errors when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleRadioChange = (name, value) => {
    setUser((prev) => {
      const newUser = { ...prev, [name]: value };

      // Clear description fields when radio buttons are set to "No"
      if (name === "criminal_record" && value === 0) {
        newUser.criminal_record_description = "";
      }
      if (name === "medical_condition" && value === 0) {
        newUser.medical_condition_description = "";
      }

      return newUser;
    });
  };

  // Helper function to validate hex color
  const isValidHexColor = (color) => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  };

  // Helper function to get valid color for display
  const getValidColor = (color) => {
    if (isValidHexColor(color)) {
      return color;
    }
    // If invalid, try to fix common issues
    if (color && color.startsWith('#') && color.length === 5) {
      // Add missing character to make it 6 digits
      return color + '0';
    }
    return '#000000'; // Default fallback
  };

  const handleColorChange = (e) => {
    setUser((prev) => ({ ...prev, color: e.target.value }));
  };

  // Handle image upload from S3ImageUpload component
  const handleImageUpload = (imageKey, file) => {
    setUser((prev) => ({
      ...prev,
      image: file, // Keep file for preview
      imageKey: imageKey, // Store S3 key for backend
      imageName: file?.name || "", // Store file name for backend validation
      imageType: file?.type || "", // Store file type for backend validation
      imageSize: file?.size || 0, // Store file size for backend validation
    }));
    setError(null); // Clear any previous errors
    // Clear image validation errors when a new image is uploaded
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors.image;
      return newErrors;
    });
  };

  // Conditional rendering helpers (matching Laravel UserFormController.js logic)
  const isGuardian = user.role_id == ROLE_IDS.GUARDIAN;
  const isClient = user.role_id == ROLE_IDS.CLIENT;
  const isContractor = user.role_id == ROLE_IDS.CONTRACTOR;
  const isStaff = user.staff == 1;

  const validate = () => {
    const newErrors = {};

    // Basic validation (matching Laravel CreateUserRequest)
    if (!user.first_name) newErrors.first_name = "First name is required";
    if (!user.last_name) newErrors.last_name = "Last name is required";
    if (!user.email) newErrors.email = "Email is required";
    if (!user.mobile_number)
      newErrors.mobile_number = "Mobile number is required";
    if (!user.role_id) newErrors.role_id = "Role is required";
    if (!user.password) newErrors.password = "Password is required";
    if (!user.confirm_password)
      newErrors.confirm_password = "Password confirmation is required";

    // Password confirmation matching validation
    if (user.password && user.confirm_password && user.password !== user.confirm_password) {
      newErrors.confirm_password = "Password and password confirmation do not match";
    }

    // Email format validation (enhanced)
    if (user.email && !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(user.email)) {
      newErrors.email = "Please enter a valid email address (e.g., user@example.com)";
    }

    // Mobile number validation (character count and format)
    if (user.mobile_number) {
      // Remove all non-numeric characters for validation
      const cleanMobileNumber = user.mobile_number.replace(/\D/g, '');
      
      if (cleanMobileNumber.length < 10) {
        newErrors.mobile_number = "Mobile number must be at least 10 digits";
      } else if (cleanMobileNumber.length > 15) {
        newErrors.mobile_number = "Mobile number cannot exceed 15 digits";
      } else if (!/^[\d\s\-+()]+$/.test(user.mobile_number)) {
        newErrors.mobile_number = "Mobile number can only contain numbers, spaces, hyphens, plus signs, and parentheses";
      }
    }

    // Image validation (matching backend validation)
    if (user.image && user.image instanceof File) {
      // File type validation
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
        "image/bmp",
      ];

      if (!allowedTypes.includes(user.image.type)) {
        newErrors.image =
          "Image must be a valid image file (JPEG, PNG, GIF, WebP, or BMP)";
      }

      // File size validation (max 100MB as per backend)
      const maxSize = 100 * 1024 * 1024; // 100MB in bytes
      if (user.image.size > maxSize) {
        newErrors.image = `Image file size must not exceed ${Math.round(
          maxSize / (1024 * 1024)
        )}MB`;
      }

      // File extension validation
      const allowedExtensions = ["jpg", "jpeg", "png", "gif", "webp", "bmp"];
      const fileExtension = user.image.name.toLowerCase().split(".").pop();
      if (!allowedExtensions.includes(fileExtension)) {
        newErrors.image =
          "Image file extension must be one of: jpg, jpeg, png, gif, webp, bmp";
      }
    }

    // Guardian-specific validation (matching Laravel validation)
    if (isGuardian) {
      if (!user.gender) newErrors.gender = "Gender must be a valid selection";
      // Gender freetext validation (required when gender is "Let me Type*")
      if (user.gender === 6 && (!user.gender_freetext || user.gender_freetext.trim() === "")) {
        newErrors.gender_freetext = "Gender description is required when 'Let me Type*' is selected";
      }
      if (user.gender_freetext && user.gender_freetext.length > 25) {
        newErrors.gender_freetext = "Gender description must not exceed 25 characters";
      }
      // Alternative email validation (optional, but must be valid format if provided)
      if (user.email_alternative && !/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(user.email_alternative)) {
        newErrors.email_alternative = "Please enter a valid alternative email address";
      }
      // Check that alternative email is not the same as primary email
      if (user.email_alternative && user.email_alternative.toLowerCase() === user.email.toLowerCase()) {
        newErrors.email_alternative = "Alternative email must be different from primary email";
      }
      if (!user.date_of_birth) {
        newErrors.date_of_birth = "Date of birth is required for guardians";
      } else if (!/^\d{2}\/\d{2}\/\d{4}$/.test(user.date_of_birth)) {
        newErrors.date_of_birth = "Date of birth must be in format dd/mm/yyyy";
      }
      if (user.criminal_record === 1 && !user.criminal_record_description) {
        newErrors.criminal_record_description =
          "Criminal record description is required when criminal record is selected";
      }
      if (user.medical_condition === 1 && !user.medical_condition_description) {
        newErrors.medical_condition_description =
          "Medical condition description is required when medical condition is selected";
      }
    }

    // Client-specific validation
    if (isClient && !user.client_id) {
      newErrors.client_id = "Client is required for client contacts";
    }

    // Contractor-specific validation
    if (isContractor) {
      if (!user.contractor_id) {
        newErrors.contractor_id = "Contractor is required for contractor contacts";
      }
      if (user.date_started_with_company && !/^\d{2}\/\d{2}\/\d{4}$/.test(user.date_started_with_company)) {
        newErrors.date_started_with_company = "Date started with company must be in format dd/mm/yyyy";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);

    // Prepare data for submission (JSON instead of FormData)
    const submitData = {
      ...user,
      // Use imageKey for the image field and include additional metadata
      image: user.imageKey || null,
      imageKey: user.imageKey,
      imageName: user.imageName || "",
      imageType: user.imageType || "",
      imageSize: user.imageSize || 0,
      imageType: user.image ? user.image.type : null,
      imageSize: user.image ? user.image.size : null,
      imageName: user.image ? user.image.name : null,
    };

    // Remove the File object from submission (keep imageKey)
    if (submitData.image instanceof File) {
      delete submitData.image;
    }

    fetch(`${process.env.REACT_APP_API_URL}/api/users`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify(submitData),
    })
      .then((res) => {
        if (!res.ok) {
          return res.json().then(errorData => {
            throw new Error(JSON.stringify(errorData));
          });
        }
        return res.json();
      })
      .then((data) => {
        console.log("✅ User created successfully:", data);
        setSuccess(true);
        setSaving(false);
        navigate(`/users/${data.user.id}`);
      })
      .catch((error) => {
        console.error("❌ Error creating user:", error);
        setSaving(false);
        
        try {
          const errorData = JSON.parse(error.message);
          
          // Handle validation errors
          if (errorData.error === "VALIDATION_ERROR" && errorData.details) {
            // Display validation errors on the form
            const validationErrors = {};
            errorData.details.forEach(detail => {
              console.log("Processing validation error:", detail);
              if (detail.includes("Email already exists") || detail.includes("email")) {
                validationErrors.email = "This email address is already registered";
              } else if (detail.includes("mobile_number") || detail.includes("Mobile")) {
                validationErrors.mobile_number = detail;
              } else if (detail.includes("first_name") || detail.includes("First")) {
                validationErrors.first_name = detail;
              } else if (detail.includes("last_name") || detail.includes("Last")) {
                validationErrors.last_name = detail;
              } else if (detail.includes("role") || detail.includes("Role")) {
                validationErrors.role_id = detail;
              } else if (detail.includes("password") || detail.includes("Password")) {
                validationErrors.password = detail;
              } else if (detail.includes("Gender") || detail.includes("gender")) {
                validationErrors.gender = detail;
              } else if (detail.includes("contractor") || detail.includes("Contractor")) {
                validationErrors.contractor_id = detail;
              } else if (detail.includes("client") || detail.includes("Client")) {
                validationErrors.client_id = detail;
              } else if (detail.includes("date_of_birth") || detail.includes("Date of birth")) {
                validationErrors.date_of_birth = detail;
              } else if (detail.includes("date_started_with_company") || detail.includes("Date started")) {
                validationErrors.date_started_with_company = detail;
              } else {
                // Generic error handling - add to general error
                setError(detail);
              }
            });
            
            console.log("Setting validation errors:", validationErrors);
            setErrors(validationErrors);
          } else if (errorData.message) {
            setError(errorData.message);
          } else {
            setError("Failed to create user");
          }
        } catch (parseError) {
          // If error parsing fails, show generic error
          setError("Failed to create user");
        }
      });
  };

  if (loading) return <Loader />;
  // if (error) return <div>{error}</div>;

  // When rendering the roles dropdown, filter roles:
  const filteredRoles = (dropdowns.roles || []).filter(
    (role) =>
      currentUser?.roleName === "System Administrator" ||
      role.name !== "System Administrator"
  );

  return (
    <div
      className="container js-UserFormController"
      style={{
        maxWidth: 900,
        margin: "0 auto",
        fontFamily: "var(--font-roboto), Arial, sans-serif",
        padding: 32,
        borderRadius: 8,
      }}
    >
      <div className="breadcrumb-row mb-4">
        <span className="breadcrumb-main" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span>
        <span className="breadcrumb-sep">/</span>
        <span
          className="breadcrumb-sep"
          style={{ background: "#36bba5", color: "#fff", padding: "2px 8px" }}
        >
          Users
        </span>
      </div>

      <div className="row">
        <div className="col-xs-12">
          <div
            className="panel panel-default panel-form"
            style={{
              background: "white",
              borderRadius: 8,
              boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
              padding: 0,
              margin: 0,
            }}
          >
            <div className="panel-heading panel-title" style={{ margin: "0" }}>
              <h3
                style={{
                  fontWeight: 600,
                  fontFamily: "var(--font-stag), serif",
                  fontSize: "29px",
                  margin: 0,
                  lineHeight: "35px",
                  letterSpacing: "-0.435px",
                }}
              >
                Add user
              </h3>
            </div>

            <div className="panel-body" style={{ padding: 32 }}>
              <form
                className="form-horizontal"
                onSubmit={handleSubmit}
                encType="multipart/form-data"
                noValidate
              >
                <fieldset className="form-horizontal">
                  {/* Role Selection - matches Laravel create.blade.php */}
                  <div
                    className={`form-group${
                      errors.role_id ? " has-error" : ""
                    }`}
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="role_id"
                      className="control-label col-sm-3 label-required"
                      style={{ textAlign: "right" }}
                    >
                      Role
                    </label>
                    <div className="col-sm-5">
                      <select
                        id="role_id"
                        name="role_id"
                        value={user.role_id}
                        onChange={handleChange}
                        className="form-control js-changeable-field"
                        required
                      >
                        <option value="">Please Select</option>
                        {filteredRoles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </select>
                      {errors.role_id && (
                        <span className="help-block text-danger">
                          {errors.role_id}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Common Fields - matches Laravel fields.blade.php exactly */}
                  <div
                    className="form-group"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="salutation"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Title
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="salutation"
                        name="salutation"
                        value={user.salutation}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div
                    className={`form-group${
                      errors.first_name ? " has-error" : ""
                    }`}
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="first_name"
                      className="control-label col-sm-3 label-required"
                      style={{ textAlign: "right" }}
                    >
                      First Name
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="first_name"
                        name="first_name"
                        value={user.first_name}
                        onChange={handleChange}
                        className="form-control"
                        required
                      />
                      {errors.first_name && (
                        <span className="help-block text-danger">
                          {errors.first_name}
                        </span>
                      )}
                    </div>
                  </div>

                  <div
                    className={`form-group${
                      errors.last_name ? " has-error" : ""
                    }`}
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="last_name"
                      className="control-label col-sm-3 label-required"
                      style={{ textAlign: "right" }}
                    >
                      Last Name
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="last_name"
                        name="last_name"
                        value={user.last_name}
                        onChange={handleChange}
                        className="form-control"
                        required
                      />
                      {errors.last_name && (
                        <span className="help-block text-danger">
                          {errors.last_name}
                        </span>
                      )}
                    </div>
                  </div>

                  <div
                    className={`form-group${errors.email ? " has-error" : ""}`}
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="email"
                      className="control-label col-sm-3 label-required"
                      style={{ textAlign: "right" }}
                    >
                      Email
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="email"
                        name="email"
                        value={user.email}
                        onChange={handleChange}
                        className="form-control"
                        placeholder="e.g., john.doe@example.com"
                        required
                        type="email"
                      />
                      {user.email && (
                        <div className="help-block" style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          {/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(user.email) ? (
                            <span style={{ color: '#5cb85c' }}>✓ Valid email format</span>
                          ) : (
                            <span style={{ color: '#d9534f' }}>Please use a valid email format</span>
                          )}
                        </div>
                      )}
                      {errors.email && (
                        <span className="help-block text-danger">
                          {errors.email}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Alternative Email - only for Guardian users */}
                  {isGuardian && (
                    <div
                      className={`form-group${
                        errors.email_alternative ? " has-error" : ""
                      }`}
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="email_alternative"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Alternative Email
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="email_alternative"
                          name="email_alternative"
                          value={user.email_alternative}
                          onChange={handleChange}
                          className="form-control"
                          placeholder="e.g., alternate@example.com"
                          type="email"
                        />
                        {user.email_alternative && (
                          <div className="help-block" style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                            {user.email_alternative.toLowerCase() === user.email.toLowerCase() ? (
                              <span style={{ color: '#d9534f' }}>Alternative email must be different from primary email</span>
                            ) : /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/.test(user.email_alternative) ? (
                              <span style={{ color: '#5cb85c' }}>✓ Valid email format</span>
                            ) : (
                              <span style={{ color: '#d9534f' }}>Please use a valid email format</span>
                            )}
                          </div>
                        )}
                        <span className="help-block" style={{ fontSize: '11px', color: '#888' }}>
                          Guardian can also login using this email address
                        </span>
                        {errors.email_alternative && (
                          <span className="help-block text-danger">
                            {errors.email_alternative}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Password fields - matches Laravel conditional logic */}
                  <div
                    className={`form-group${
                      errors.password ? " has-error" : ""
                    }`}
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="password"
                      className="control-label col-sm-3 label-required"
                      style={{ textAlign: "right" }}
                    >
                      Password
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="password"
                        name="password"
                        type="password"
                        value={user.password}
                        onChange={handleChange}
                        className="form-control"
                        autoComplete="new-password"
                        required
                      />
                      {errors.password && (
                        <span className="help-block text-danger">
                          {errors.password}
                        </span>
                      )}
                      {/* <span className="help-block">Leave empty if you don't want to change password</span> */}
                    </div>
                  </div>

                  <div
                    className={`form-group${
                      errors.confirm_password ? " has-error" : ""
                    }`}
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="confirm_password"
                      className="control-label col-sm-3 label-required"
                      style={{ textAlign: "right" }}
                    >
                      Password Confirmation
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="confirm_password"
                        name="confirm_password"
                        type="password"
                        value={user.confirm_password}
                        onChange={handleChange}
                        className="form-control"
                        autoComplete="new-password"
                        required
                      />
                      {errors.confirm_password && (
                        <span className="help-block text-danger">
                          {errors.confirm_password}
                        </span>
                      )}

                      {/* <span className="help-block">Leave empty if you don't want to change password</span> */}
                    </div>
                  </div>

                  {/* Photograph - using S3ImageUpload component */}
                  <div
                    className={`form-group${errors.image ? " has-error" : ""}`}
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="image"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Photograph
                    </label>
                    <div className="col-sm-5">
                      <S3ImageUpload
                        onImageUpload={handleImageUpload}
                        currentImage={user.image}
                        folder="uploads/images/users"
                        helpText="The photograph must be a clear headshot of true likeness of yourself. The file can be no larger than 100MB"
                        maxSize={100 * 1024 * 1024} // 100MB
                        accept="image/*"
                        showPreview={true}
                        previewClassName="img-responsive mb-20"
                        exampleImage="/images/photograph-woman.jpg"
                      />
                      {errors.image && (
                        <span className="help-block text-danger">
                          {errors.image}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="clearfix"></div>

                  {/* Job Title */}
                  <div
                    className="form-group"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="job_title"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Job Title
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="job_title"
                        name="job_title"
                        value={user.job_title}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Phone Number */}
                  <div
                    className="form-group"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="phone_number"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Phone Number
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="phone_number"
                        name="phone_number"
                        value={user.phone_number}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        onInput={handleInput}
                        className="form-control"
                        placeholder="e.g., 020 7123 4567"
                        inputMode="tel"
                        pattern="[0-9\s\-+()]+"
                      />
                    </div>
                  </div>

                  {/* Mobile Number */}
                  <div
                    className={`form-group${
                      errors.mobile_number ? " has-error" : ""
                    }`}
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="mobile_number"
                      className="control-label col-sm-3 label-required"
                      style={{ textAlign: "right" }}
                    >
                      Mobile Number
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="mobile_number"
                        name="mobile_number"
                        value={user.mobile_number}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        onPaste={handlePaste}
                        onInput={handleInput}
                        className="form-control"
                        placeholder="e.g., +44 7123 456789 or 07123456789"
                        inputMode="tel"
                        pattern="[0-9\s\-+()]+"
                        required
                      />
                      <div className="help-block" style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        Characters: {user.mobile_number.replace(/\D/g, '').length} digits
                        {user.mobile_number.replace(/\D/g, '').length < 10 && (
                          <span style={{ color: '#d9534f' }}> (minimum 10 digits required)</span>
                        )}
                        {user.mobile_number.replace(/\D/g, '').length > 15 && (
                          <span style={{ color: '#d9534f' }}> (maximum 15 digits allowed)</span>
                        )}
                        {user.mobile_number.replace(/\D/g, '').length >= 10 && user.mobile_number.replace(/\D/g, '').length <= 15 && (
                          <span style={{ color: '#5cb85c' }}> ✓</span>
                        )}
                      </div>
                      {errors.mobile_number && (
                        <span className="help-block text-danger">
                          {errors.mobile_number}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Address fields */}
                  <div
                    className="form-group"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="address_1"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Address 1
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="address_1"
                        name="address_1"
                        value={user.address_1}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div
                    className="form-group"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="address_2"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Address 2
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="address_2"
                        name="address_2"
                        value={user.address_2}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div
                    className="form-group"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="address_3"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Address 3
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="address_3"
                        name="address_3"
                        value={user.address_3}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div
                    className="form-group"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="town"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Town
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="town"
                        name="town"
                        value={user.town}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  <div
                    className="form-group"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="postcode"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Postcode
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="postcode"
                        name="postcode"
                        value={user.postcode}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Fax Number - conditional for client/contractor */}
                  <div
                    className={`form-group${
                      isClient || isContractor ? "" : " d-none"
                    }`}
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="fax_number"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Fax Number
                    </label>
                    <div className="col-sm-5">
                      <input
                        id="fax_number"
                        name="fax_number"
                        value={user.fax_number}
                        onChange={handleChange}
                        className="form-control"
                      />
                    </div>
                  </div>

                  {/* Staff and Display Options - matches Laravel permission check */}
                  <div
                    className="form-group"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Staff
                    </label>
                    <div className="col-sm-5">
                      <label style={{ marginRight: 10 }}>
                        <input
                          type="radio"
                          name="staff"
                          value="0"
                          checked={user.staff === 0}
                          onChange={() => handleRadioChange("staff", 0)}
                          className="form-checkbox"
                        />{" "}
                        No
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="staff"
                          value="1"
                          checked={user.staff === 1}
                          onChange={() => handleRadioChange("staff", 1)}
                          className="form-checkbox"
                        />{" "}
                        Yes
                      </label>
                    </div>
                  </div>

                  {/* Staff-only fields - matches Laravel js-staff-fields logic */}
                  {isStaff && (
                    <>
                      <div
                        className="form-group"
                        style={{
                          flexDirection: "row",
                          alignContent: "center",
                          gap: 40,
                          marginBottom: "12px",
                        }}
                      >
                        <label
                          className="control-label col-sm-3"
                          style={{ textAlign: "right" }}
                        >
                          Display on Page Guardian
                        </label>
                        <div className="col-sm-5">
                          <label style={{ marginRight: 10 }}>
                            <input
                              type="radio"
                              name="staff_guardian_page"
                              value="0"
                              checked={user.staff_guardian_page === 0}
                              onChange={() =>
                                handleRadioChange("staff_guardian_page", 0)
                              }
                              className="form-checkbox"
                            />{" "}
                            No
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="staff_guardian_page"
                              value="1"
                              checked={user.staff_guardian_page === 1}
                              onChange={() =>
                                handleRadioChange("staff_guardian_page", 1)
                              }
                              className="form-checkbox"
                            />{" "}
                            Yes
                          </label>
                        </div>
                      </div>

                      {/* Staff-only: certificate review access */}
                      <div
                        className="form-group"
                        style={{
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 40,
                          marginBottom: "12px",
                        }}
                      >
                        <label
                          className="control-label col-sm-3"
                          style={{ textAlign: "right" }}
                        >
                          Can review certificates
                        </label>
                        <div className="col-sm-5">
                          <label>
                            <input
                              type="checkbox"
                              name="can_review_certificates"
                              checked={user.can_review_certificates === 1}
                              onChange={(e) =>
                                handleRadioChange(
                                  "can_review_certificates",
                                  e.target.checked ? 1 : 0
                                )
                              }
                              className="form-checkbox"
                            />{" "}
                            Allow this user to review certificates
                          </label>
                        </div>
                      </div>

                      <div
                        className="form-group"
                        style={{
                          flexDirection: "row",
                          alignContent: "center",
                          gap: 40,
                          marginBottom: "12px",
                        }}
                      >
                        <label
                          className="control-label col-sm-3"
                          style={{ textAlign: "right" }}
                        >
                          Display on Property Owners
                        </label>
                        <div className="col-sm-5">
                          <label style={{ marginRight: 10 }}>
                            <input
                              type="radio"
                              name="staff_property_owners"
                              value="0"
                              checked={user.staff_property_owners === 0}
                              onChange={() =>
                                handleRadioChange("staff_property_owners", 0)
                              }
                              className="form-checkbox"
                            />{" "}
                            No
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="staff_property_owners"
                              value="1"
                              checked={user.staff_property_owners === 1}
                              onChange={() =>
                                handleRadioChange("staff_property_owners", 1)
                              }
                              className="form-checkbox"
                            />{" "}
                            Yes
                          </label>
                        </div>
                      </div>

                      <div
                        className="form-group"
                        style={{
                          flexDirection: "row",
                          alignContent: "center",
                          gap: 40,
                          marginBottom: "12px",
                        }}
                      >
                        <label
                          className="control-label col-sm-3"
                          style={{ textAlign: "right" }}
                        >
                          Display on About Us
                        </label>
                        <div className="col-sm-5">
                          <label style={{ marginRight: 10 }}>
                            <input
                              type="radio"
                              name="staff_about_us_page"
                              value="0"
                              checked={user.staff_about_us_page === 0}
                              onChange={() =>
                                handleRadioChange("staff_about_us_page", 0)
                              }
                              className="form-checkbox"
                            />{" "}
                            No
                          </label>
                          <label>
                            <input
                              type="radio"
                              name="staff_about_us_page"
                              value="1"
                              checked={user.staff_about_us_page === 1}
                              onChange={() =>
                                handleRadioChange("staff_about_us_page", 1)
                              }
                              className="form-checkbox"
                            />{" "}
                            Yes
                          </label>
                        </div>
                      </div>

                      <div
                        className="form-group"
                        style={{
                          flexDirection: "row",
                          alignContent: "center",
                          gap: 40,
                          marginBottom: "12px",
                        }}
                      >
                        <label
                          htmlFor="department_id"
                          className="control-label col-sm-3"
                          style={{ textAlign: "right" }}
                        >
                          Department
                        </label>
                        <div className="col-sm-5">
                          <select
                            id="department_id"
                            name="department_id"
                            value={user.department_id}
                            onChange={handleChange}
                            className="form-control"
                          >
                            <option value="">Please Select</option>
                            {dropdowns.departments.map((d) => (
                              <option key={d.id} value={d.id}>
                                {d.name}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div
                        className="form-group"
                        style={{
                          flexDirection: "row",
                          alignContent: "center",
                          gap: 40,
                          marginBottom: "12px",
                        }}
                      >
                        <label
                          htmlFor="biography"
                          className="control-label col-sm-3"
                          style={{ textAlign: "right" }}
                        >
                          Biography
                        </label>
                        <div className="col-sm-9">
                          <CKEditor
                            editor={ClassicEditor}
                            data={user.biography}
                            config={{
                            licenseKey: "GPL",
                              height: "200px",
                              toolbar: [
                                "heading",
                                "|",
                                "bold",
                                "italic",
                                "link",
                                "bulletedList",
                                "numberedList",
                                "|",
                                "outdent",
                                "indent",
                                "|",
                                "blockQuote",
                                "insertTable",
                                "undo",
                                "redo",
                              ],
                              placeholder: "Enter biography here...",
                              removePlugins: [
                                "CKFinderUploadAdapter",
                                "CKFinder",
                                "EasyImage",
                                "Image",
                                "ImageCaption",
                                "ImageStyle",
                                "ImageToolbar",
                                "ImageUpload",
                              ],
                            }}
                            onChange={(event, editor) => {
                              const data = editor.getData();
                              setUser((prev) => ({ ...prev, biography: data }));
                            }}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Calendar Color */}
                  <div
                    className="form-group"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      htmlFor="color"
                      className="control-label col-sm-3"
                      style={{ textAlign: "right" }}
                    >
                      Calendar Color
                    </label>
                    <div className="col-sm-5">
                      <div className="input-group colorpicker-component" style={{ display: "flex", alignItems: "center" }}>
                        <input
                          id="color"
                          name="color"
                          type="text"
                          value={user.color}
                          onChange={handleColorChange}
                          className="form-control"
                          style={{ width: "120px", height: "38px", marginRight: "10px" }}
                          placeholder="#000000"
                        />
                        <input
                          type="color"
                          value={getValidColor(user.color)}
                          onChange={handleColorChange}
                          style={{ width: "40px", height: "38px", border: "1px solid #ccc", borderRadius: "3px" }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Notification Options */}
                  <div
                    className="form-group"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      className="control-label col-sm-4"
                      style={{ textAlign: "right" }}
                    >
                      Receive notification of property status
                    </label>
                    <div className="col-sm-5">
                      <label style={{ marginRight: 10 }}>
                        <input
                          type="radio"
                          name="rec_notif_of_property_status"
                          value="0"
                          checked={user.rec_notif_of_property_status === 0}
                          onChange={() =>
                            handleRadioChange("rec_notif_of_property_status", 0)
                          }
                          className="form-checkbox"
                        />{" "}
                        No
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="rec_notif_of_property_status"
                          value="1"
                          checked={user.rec_notif_of_property_status === 1}
                          onChange={() =>
                            handleRadioChange("rec_notif_of_property_status", 1)
                          }
                          className="form-checkbox"
                        />{" "}
                        Yes
                      </label>
                    </div>
                  </div>

                  <div
                    className="form-group"
                    style={{
                      flexDirection: "row",
                      alignContent: "center",
                      gap: 40,
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      className="control-label col-sm-4"
                      style={{ textAlign: "right" }}
                    >
                      Receive notification of property ID
                    </label>
                    <div className="col-sm-5">
                      <label style={{ marginRight: 10 }}>
                        <input
                          type="radio"
                          name="rec_notif_of_property_id"
                          value="0"
                          checked={user.rec_notif_of_property_id === 0}
                          onChange={() =>
                            handleRadioChange("rec_notif_of_property_id", 0)
                          }
                          className="form-checkbox"
                        />{" "}
                        No
                      </label>
                      <label>
                        <input
                          type="radio"
                          name="rec_notif_of_property_id"
                          value="1"
                          checked={user.rec_notif_of_property_id === 1}
                          onChange={() =>
                            handleRadioChange("rec_notif_of_property_id", 1)
                          }
                          className="form-checkbox"
                        />{" "}
                        Yes
                      </label>
                    </div>
                  </div>

                  {/* Gender - only visible for Guardian users */}
                  {isGuardian && (
                    <div
                      className={`form-group${
                        errors.gender ? " has-error" : ""
                      }`}
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="gender"
                        className="control-label col-sm-3 label-required"
                        style={{ textAlign: "right" }}
                      >
                        Gender
                      </label>
                      <div className="col-sm-5">
                        <select
                          id="gender"
                          name="gender"
                          value={user.gender}
                          onChange={handleChange}
                          className="form-control"
                          required
                        >
                          <option value="">Please Select</option>
                          {dropdowns.genders.map((g) => (
                              <option key={g.id} value={g.id}>
                                {g.name}
                              </option>
                            ))}
                        </select>
                        {errors.gender && (
                          <span className="help-block text-danger">
                            {errors.gender}
                          </span>
                        )}
                      </div>
                      {user.gender === 6 && (
                        <div className="col-sm-3">
                          <input
                            className={`form-control ${errors.gender_freetext ? 'has-error' : ''}`}
                            type="text"
                            id="gender_freetext"
                            name="gender_freetext"
                            value={user.gender_freetext}
                            onChange={handleChange}
                            placeholder="Please specify gender"
                            maxLength="25"
                            required
                          />
                          {errors.gender_freetext && (
                            <span className="help-block text-danger">
                              {errors.gender_freetext}
                            </span>
                          )}
                          <div className="help-block">
                            Maximum 25 characters
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </fieldset>

                {/* Guardian Fields - matches Laravel fields-guardian.blade.php */}
                {isGuardian && (
                  <>
                    {/* Nationality */}
                    <div
                      className="form-group"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="country_id"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Nationality
                      </label>
                      <div className="col-sm-5">
                        <select
                          id="country_id"
                          name="country_id"
                          value={user.country_id}
                          onChange={handleChange}
                          className="form-control"
                        >
                          <option value="">Please Select</option>
                          {dropdowns.nationalities.map((n) => (
                            <option key={n.id} value={n.id}>
                              {n.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {/* Religion */}
                    <div
                      className="form-group"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="religion_id"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Religion
                      </label>
                      <div className="col-sm-5">
                        <select
                          id="religion_id"
                          name="religion_id"
                          value={user.religion_id}
                          onChange={handleChange}
                          className="form-control"
                        >
                          <option value="">Please Select</option>
                          {dropdowns.religions.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {/* How did you hear about Global Guardians? */}
                    <div
                      className="form-group"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="hear_about_id"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        How did you hear about Global Guardians?
                      </label>
                      <div className="col-sm-5">
                        <select
                          id="hear_about_id"
                          name="hear_about_id"
                          value={user.hear_about_id}
                          onChange={handleChange}
                          className="form-control"
                        >
                          <option value="">Please Select</option>
                          {dropdowns.hearAbouts.map((h) => (
                            <option key={h.id} value={h.id}>
                              {h.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {/* Date of Birth */}
                    <div
                      className={`form-group${
                        errors.date_of_birth ? " has-error" : ""
                      }`}
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="date_of_birth"
                        className="control-label col-sm-3 label-required"
                        style={{ textAlign: "right" }}
                      >
                        Date of Birth
                      </label>
                      <div className="col-sm-5">
                                                 <input
                           id="date_of_birth"
                           name="date_of_birth"
                           type="date"
                           value={convertToISODate(user.date_of_birth)}
                           onChange={handleChange}
                           className="form-control"
                           max={new Date().toISOString().split('T')[0]}
                         />
                        {errors.date_of_birth && (
                          <span className="help-block text-danger">
                            {errors.date_of_birth}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* National Insurance Number */}
                    <div
                      className="form-group"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="national_insurance_number"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        National Insurance Number
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="national_insurance_number"
                          name="national_insurance_number"
                          value={user.national_insurance_number}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                    {/* Any Criminal Record? */}
                    <div
                      className="form-group"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Any Criminal Record?
                      </label>
                      <div className="col-sm-5">
                        <label style={{ marginRight: 10 }}>
                          <input
                            type="radio"
                            name="criminal_record"
                            value="0"
                            checked={user.criminal_record === 0}
                            onChange={() =>
                              handleRadioChange("criminal_record", 0)
                            }
                            className="form-checkbox"
                          />{" "}
                          No
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="criminal_record"
                            value="1"
                            checked={user.criminal_record === 1}
                            onChange={() =>
                              handleRadioChange("criminal_record", 1)
                            }
                            className="form-checkbox"
                          />{" "}
                          Yes
                        </label>
                      </div>
                    </div>
                    {/* Criminal Record Description */}
                    {user.criminal_record === 1 && (
                      <div
                        className={`form-group${
                          errors.criminal_record_description ? " has-error" : ""
                        }`}
                        style={{
                          flexDirection: "row",
                          alignContent: "center",
                          gap: 40,
                          marginBottom: "12px",
                        }}
                      >
                        <label
                          htmlFor="criminal_record_description"
                          className="control-label col-sm-3"
                          style={{ textAlign: "right" }}
                        >
                          Criminal Record Description
                        </label>
                        <div className="col-sm-5">
                          <textarea
                            id="criminal_record_description"
                            name="criminal_record_description"
                            value={user.criminal_record_description}
                            onChange={handleChange}
                            className="form-control"
                          />
                          {errors.criminal_record_description && (
                            <span className="help-block text-danger">
                              {errors.criminal_record_description}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Any Medical Condition? */}
                    <div
                      className="form-group"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Any Medical Condition?
                      </label>
                      <div className="col-sm-5">
                        <label style={{ marginRight: 10 }}>
                          <input
                            type="radio"
                            name="medical_condition"
                            value="0"
                            checked={user.medical_condition === 0}
                            onChange={() =>
                              handleRadioChange("medical_condition", 0)
                            }
                            className="form-checkbox"
                          />{" "}
                          No
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="medical_condition"
                            value="1"
                            checked={user.medical_condition === 1}
                            onChange={() =>
                              handleRadioChange("medical_condition", 1)
                            }
                            className="form-checkbox"
                          />{" "}
                          Yes
                        </label>
                      </div>
                    </div>
                    {/* Medical Condition Description */}
                    {user.medical_condition === 1 && (
                      <div
                        className={`form-group${
                          errors.medical_condition_description
                            ? " has-error"
                            : ""
                        }`}
                        style={{
                          flexDirection: "row",
                          alignContent: "center",
                          gap: 40,
                          marginBottom: "12px",
                        }}
                      >
                        <label
                          htmlFor="medical_condition_description"
                          className="control-label col-sm-3"
                          style={{ textAlign: "right" }}
                        >
                          Medical Condition Description
                        </label>
                        <div className="col-sm-5">
                          <textarea
                            id="medical_condition_description"
                            name="medical_condition_description"
                            value={user.medical_condition_description}
                            onChange={handleChange}
                            className="form-control"
                          />
                          {errors.medical_condition_description && (
                            <span className="help-block text-danger">
                              {errors.medical_condition_description}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {/* Details of Next of Kin */}
                    <div
                      className="form-group"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="next_of_kin"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Details of Next of Kin
                      </label>
                      <div className="col-sm-5">
                        <textarea
                          id="next_of_kin"
                          name="next_of_kin"
                          value={user.next_of_kin}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                    {/* Notes */}
                    <div
                      className="form-group"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="notes"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Notes
                      </label>
                      <div className="col-sm-5">
                        <textarea
                          id="notes"
                          name="notes"
                          value={user.notes}
                          onChange={handleChange}
                          className="form-control"
                          rows={16}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Client Contact Fields - matches Laravel fields-client-contact.blade.php */}
                {isClient && (
                  <>
                    {/* Client */}
                    <div
                      className={`form-group${
                        errors.client_id ? " has-error" : ""
                      }`}
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="client_id"
                        className="control-label col-sm-3 label-required"
                        style={{ textAlign: "right" }}
                      >
                        Client
                      </label>
                      <div className="col-sm-5">
                        <select
                          id="client_id"
                          name="client_id"
                          value={user.client_id}
                          onChange={handleChange}
                          className="form-control"
                        >
                          <option value="">Please Select</option>
                          {dropdowns.clients.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                        {errors.client_id && (
                          <span className="help-block text-danger">
                            {errors.client_id}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Position */}
                    <div
                      className="form-group"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="position"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Position
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="position"
                          name="position"
                          value={user.position}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                    {/* Department */}
                    <div
                      className="form-group"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="department"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Department
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="department"
                          name="department"
                          value={user.department}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                    {/* Reports To */}
                    <div
                      className="form-group"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="reports_to"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Reports To
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="reports_to"
                          name="reports_to"
                          value={user.reports_to}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                    {/* Description */}
                    <div
                      className="form-group"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="description"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Description
                      </label>
                      <div className="col-sm-5">
                        <textarea
                          id="description"
                          name="description"
                          value={user.description}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>
                    {/* Access to NTQ */}
                    <div
                      className="form-group"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Access To NTQ
                      </label>
                      <div className="col-sm-5">
                        <label style={{ marginRight: 10 }}>
                          <input
                            type="radio"
                            name="access_to_ntq"
                            value="0"
                            checked={user.access_to_ntq === 0}
                            onChange={() =>
                              setUser((prev) => ({ ...prev, access_to_ntq: 0 }))
                            }
                            className="form-checkbox"
                          />{" "}
                          No
                        </label>
                        <label>
                          <input
                            type="radio"
                            name="access_to_ntq"
                            value="1"
                            checked={user.access_to_ntq === 1}
                            onChange={() =>
                              setUser((prev) => ({ ...prev, access_to_ntq: 1 }))
                            }
                            className="form-checkbox"
                          />{" "}
                          Yes
                        </label>
                      </div>
                    </div>
                  </>
                )}

                {/* Contractor Contact Fields - matches Laravel fields-contractor-contact.blade.php */}
                {isContractor && (
                  <>
                    <div
                      className={`form-group${
                        errors.contractor_id ? " has-error" : ""
                      }`}
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="contractor_id"
                        className="control-label col-sm-3 label-required"
                        style={{ textAlign: "right" }}
                      >
                        Contractor
                      </label>
                      <div className="col-sm-5">
                        <select
                          id="contractor_id"
                          name="contractor_id"
                          value={user.contractor_id}
                          onChange={handleChange}
                          className="form-control"
                        >
                          <option value="">Please Select</option>
                          {dropdowns.contractors.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </select>
                        {errors.contractor_id && (
                          <span className="help-block text-danger">
                            {errors.contractor_id}
                          </span>
                        )}
                      </div>
                    </div>

                    <div
                      className="form-group"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="contractor_position"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Position
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="contractor_position"
                          name="contractor_position"
                          value={user.contractor_position}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div
                      className="form-group"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="contractor_department"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Department
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="contractor_department"
                          name="contractor_department"
                          value={user.contractor_department}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div
                      className="form-group"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="contractor_reports_to"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Reports To
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="contractor_reports_to"
                          name="contractor_reports_to"
                          value={user.contractor_reports_to}
                          onChange={handleChange}
                          className="form-control"
                        />
                      </div>
                    </div>

                    <div
                      className="form-group"
                      style={{
                        flexDirection: "row",
                        alignContent: "center",
                        gap: 40,
                        marginBottom: "12px",
                      }}
                    >
                      <label
                        htmlFor="date_started_with_company"
                        className="control-label col-sm-3"
                        style={{ textAlign: "right" }}
                      >
                        Date Started With Company
                      </label>
                      <div className="col-sm-5">
                        <input
                          id="date_started_with_company"
                          name="date_started_with_company"
                          type="text"
                          value={user.date_started_with_company || ""}
                          onChange={handleChange}
                          placeholder="dd/mm/yyyy"
                          className={`form-control${errors.date_started_with_company ? " has-error" : ""}`}
                        />
                        {errors.date_started_with_company && (
                          <span className="help-block text-danger">
                            {errors.date_started_with_company}
                          </span>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Submit Button */}
                <fieldset className="form-horizontal">
                  <div
                    className="form-group text-right"
                    style={{ marginTop: 32 }}
                  >
                    <div className="col-sm-3"></div>
                    <div
                      style={{
                        display: "flex",
                        alignSelf: "center",
                        width: "100%",
                        justifyContent: "center",
                      }}
                    >
                      {/* <button type="submit" className="btn btn-head btn-standard" style={{ minWidth: 120 }} disabled={saving}>
                        <i className="glyphicon glyphicon-plus"></i> {saving ? 'Saving...' : 'Add user'}
                      </button> */}
                      {/* <div style={{display: 'flex', alignSelf: 'center', justifyContent: 'right'}}> */}
                      <button
                        type="submit"
                        className="btn btn-add-user"
                        // onClick={() => navigate("/users/create")}
                        style={{ minWidth: 120 }}
                        disabled={saving}
                      >
                        <FaPlus style={{ marginRight: 8 }} />
                        {saving ? "Saving..." : "Add user"}
                      </button>
                    </div>

                    {success && (
                      <span className="text-success ml-2">Saved!</span>
                    )}
                    {/* </div> */}
                  </div>
                </fieldset>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserCreate;
